import React, { createContext, useContext, useMemo, useState, useCallback, useEffect } from 'react';
import apiService, { TOKEN_CHANGE_EVENT } from '../services/api';
import { API_ENDPOINTS } from '../utils/apiUrls';

const AuthContext = createContext(null);

const hasTokenInStorage = () => {
  if (typeof window === 'undefined') return false;
  try {
    return Boolean(localStorage.getItem('access_token'));
  } catch {
    return false;
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loadingAuth, setLoadingAuth] = useState(false);
  const [hasAccessToken, setHasAccessToken] = useState(() => hasTokenInStorage());
  const [clientRoomStatus, setClientRoomStatus] = useState(null);
  const [countryCode, setCountryCode] = useState(null);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const updateTokenState = () => setHasAccessToken(hasTokenInStorage());
    updateTokenState();

    window.addEventListener(TOKEN_CHANGE_EVENT, updateTokenState);
    window.addEventListener('storage', updateTokenState);

    return () => {
      window.removeEventListener(TOKEN_CHANGE_EVENT, updateTokenState);
      window.removeEventListener('storage', updateTokenState);
    };
  }, []);

  const fetchMe = useCallback(async () => {
    const me = await apiService.get(API_ENDPOINTS.AUTH.ME);
    setUser(me);
    try {
      if (me?.tier != null) localStorage.setItem('user_tier', me.tier);
      if (me?.admin_email) localStorage.setItem('admin_email', me.admin_email);
    } catch {
      // ignore storage errors
    }
    return me;
  }, []);

  const clearSession = useCallback(() => {
    apiService.clearTokens();
    setUser(null);
    setHasAccessToken(false);
    setClientRoomStatus(null);
    setCountryCode(null);
  }, []);

  const loginByCRMToken = useCallback(async (token) => {
    if (!token || typeof token !== 'string') {
      throw new Error('missing_crm_token');
    }
    setLoadingAuth(true);
    try {
      const login = await apiService.post(
        API_ENDPOINTS.AUTH.CRM_SSO,
        { token },
        { skipAuth: true, skipAuthRefresh: true }
      );

      if (login?.authenticated !== true) {
        throw new Error('crm_auth_failed');
      }

      const status = login?.client_room_status;
      setClientRoomStatus(status || null);
      setCountryCode(login?.country_code || null);

      if (status === 'not_registered') {
        apiService.clearTokens();
        setUser(null);
        setHasAccessToken(false);
        return login;
      }

      if (status !== 'active' || !login?.access_token || !login?.refresh_token) {
        throw new Error('invalid_session_response');
      }

      apiService.saveTokens({
        access_token: login.access_token,
        refresh_token: login.refresh_token,
      });
      setHasAccessToken(true);
      await fetchMe();
      return login;
    } catch (error) {
      apiService.clearTokens();
      setUser(null);
      setHasAccessToken(false);
      setClientRoomStatus(null);
      setCountryCode(null);
      throw error;
    } finally {
      setLoadingAuth(false);
    }
  }, [fetchMe]);


  const value = useMemo(
    () => ({
      user,
      loadingAuth,
      loginByCRMToken,
      clearSession,
      fetchMe,
      hasAccessToken,
      clientRoomStatus,
      countryCode,
    }),
    [
      user,
      loadingAuth,
      loginByCRMToken,
      clearSession,
      fetchMe,
      hasAccessToken,
      clientRoomStatus,
      countryCode,
    ]
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
